import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { EstadoGatewayConciliacao, FiltroPeriodo } from '../../../../components/financeiro';
import { useConciliacaoIugu } from '../../../../hooks/useConciliacaoIugu';
import { formatCurrency } from '../../../../utils/money';

// Aba "Conciliação" do hub financeiro admin -- financeiro-conciliacao-iugu Phase 5 (Task 7),
// AC-13/AC-14. Substitui o esqueleto estático herdado de financeiro-dashboard-plataforma Phase 5
// (25 linhas, texto fixo). Consome useConciliacaoIugu() -- nenhum import de GraphQL/services/
// diretamente (VALIDATE dedicado). Dois blocos independentes (saldo, divergências), cada um com
// seu próprio EstadoGatewayConciliacao (consultadoEm + ação de atualizar isolados, AC-14). O
// bloco de divergências tem uma cascata de 4 estados mutuamente exclusivos -- erro de rede,
// vazio-por-falta-de-dados, gateway indisponível, e o positivo "nenhuma divergência" -- nunca
// compartilhando a mesma mensagem (docs/anti-patterns.md:207-230), mais um 5º estado (a tabela).

const CLASSES_CHARGEBACK = new Set(['REEMBOLSO_COM_CHARGEBACK', 'CHARGEBACK_DETECTADO']);

// Mesmo formato de EstadoGatewayConciliacao.tsx ("Dados de DD/MM/YYYY HH:mm") -- a idade do dado
// desta tela já é lida nesse formato pelo operador.
function formatarInstante(valor: string | null): string | null {
  return valor ? dayjs(valor).format('DD/MM/YYYY HH:mm') : null;
}

const ConciliacaoTab: React.FC = () => {
  const {
    periodo,
    setPeriodo,
    resultado,
    conciliacaoLoading,
    conciliacaoError,
    refetchConciliacao,
    saldo,
    saldoConsultadoEm,
    saldoLoading,
    saldoError,
    refetchSaldo,
    reconciliar,
    reconciliando,
    reconciliarAviso,
    limparReconciliarAviso,
  } = useConciliacaoIugu();

  const divergencias = resultado?.divergencias ?? [];
  const temChargebackNoPeriodo = divergencias.some((d) => CLASSES_CHARGEBACK.has(d.classe));

  return (
    <Box>
      <Box data-testid="conciliacao-bloco-saldo" mb={4}>
        <Typography variant="h6" mb={1}>
          Saldo (conta mestre InPlay)
        </Typography>
        <EstadoGatewayConciliacao
          consultadoEm={saldoConsultadoEm}
          gatewayDisponivel={true}
          onAtualizar={() => refetchSaldo()}
          atualizando={saldoLoading}
        />
        {saldoError ? (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={() => refetchSaldo()}>
                Tentar novamente
              </Button>
            }
          >
            Erro ao consultar o saldo da conta mestre. Tente novamente.
          </Alert>
        ) : saldoLoading ? (
          <CircularProgress size={24} />
        ) : (
          <Typography variant="h5">{formatCurrency(saldo)}</Typography>
        )}
      </Box>

      <Box mb={3}>
        <FiltroPeriodo periodo={periodo} onChange={setPeriodo} />
      </Box>

      <Box data-testid="conciliacao-bloco-divergencias">
        <Typography variant="h6" mb={1}>
          Divergências no período
        </Typography>
        <EstadoGatewayConciliacao
          consultadoEm={resultado?.consultadoEm ?? null}
          gatewayDisponivel={resultado?.gatewayDisponivel ?? true}
          onAtualizar={() => refetchConciliacao()}
          atualizando={conciliacaoLoading}
        />

        {conciliacaoError ? (
          <Box textAlign="center" py={5} data-testid="conciliacao-divergencias-erro">
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={() => refetchConciliacao()}>
                  Tentar novamente
                </Button>
              }
            >
              Erro ao carregar divergências de conciliação. Tente novamente.
            </Alert>
          </Box>
        ) : conciliacaoLoading ? (
          <Box
            display="flex"
            justifyContent="center"
            py={4}
            data-testid="conciliacao-divergencias-loading"
          >
            <CircularProgress />
          </Box>
        ) : resultado === null ? (
          <Box textAlign="center" py={5} data-testid="conciliacao-divergencias-indisponivel">
            Dados de conciliação indisponíveis no momento.
          </Box>
        ) : resultado.gatewayDisponivel === false ? (
          <Alert severity="warning" data-testid="conciliacao-gateway-indisponivel">
            Gateway da Iugu indisponível para a varredura no momento — tente novamente mais
            tarde.
          </Alert>
        ) : divergencias.length === 0 ? (
          <Alert severity="success" data-testid="conciliacao-divergencias-vazio">
            Nenhuma divergência no período selecionado.
          </Alert>
        ) : (
          <>
            {reconciliarAviso && (
              <Alert
                severity={reconciliarAviso.severidade}
                sx={{ mb: 2 }}
                data-testid="conciliacao-reconciliar-aviso"
                onClose={limparReconciliarAviso}
              >
                {reconciliarAviso.mensagem}
              </Alert>
            )}
            {temChargebackNoPeriodo && (
              <Alert severity="error" sx={{ mb: 2 }}>
                Atenção: existem divergências de reembolso com chargeback no período — risco de
                devolução dupla.
              </Alert>
            )}
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Classe</TableCell>
                    <TableCell>Status local</TableCell>
                    <TableCell>Status remoto</TableCell>
                    <TableCell>Valor local</TableCell>
                    <TableCell>Valor remoto</TableCell>
                    <TableCell>Última conciliação</TableCell>
                    <TableCell>Ação</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {divergencias.map((d, idx) => {
                    const pagamentoId = d.pagamentoId;
                    // A Iugu já respondeu 404 para esta invoice numa tentativa anterior: o fato
                    // ficou gravado no registro, então a linha diz isso explicitamente e a ação
                    // não é mais oferecida (o backend também a recusa, sem tocar a rede).
                    const semInvoiceNaIugu = d.invoiceInexistenteEm != null;
                    const ultimaConciliacao = formatarInstante(d.validadoIuguEm);
                    return (
                      <TableRow key={pagamentoId ?? d.invoiceId ?? idx}>
                        <TableCell>{d.classe}</TableCell>
                        <TableCell>{d.statusLocal ?? '-'}</TableCell>
                        <TableCell>{d.statusRemoto ?? '-'}</TableCell>
                        <TableCell>
                          {d.valorLocal != null ? formatCurrency(d.valorLocal) : '-'}
                        </TableCell>
                        <TableCell>
                          {d.valorRemoto != null ? formatCurrency(d.valorRemoto) : '-'}
                        </TableCell>
                        <TableCell>
                          {/* "Nunca conciliada" é diferente de "conciliada há muito tempo" --
                              nunca a mesma célula vazia para os dois. */}
                          {ultimaConciliacao ?? 'Nunca conciliada'}
                        </TableCell>
                        <TableCell>
                          {semInvoiceNaIugu ? (
                            <Chip
                              size="small"
                              color="warning"
                              variant="outlined"
                              label="Sem invoice na Iugu"
                              title={
                                formatarInstante(d.invoiceInexistenteEm)
                                  ? `Iugu confirmou em ${formatarInstante(d.invoiceInexistenteEm)}`
                                  : undefined
                              }
                              data-testid="conciliacao-sem-invoice"
                            />
                          ) : (
                            pagamentoId && (
                              <Button
                                size="small"
                                disabled={reconciliando}
                                onClick={() => reconciliar(pagamentoId)}
                              >
                                Reconciliar
                              </Button>
                            )
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Box>
    </Box>
  );
};

export default ConciliacaoTab;
